/**
 * Copy-merge progression.
 *
 * Two identical copies of the same level produce one copy of the next level.
 * Therefore the number of level-1 copies needed to reach `targetLevel` is
 *
 *     requiredCopies = 2 ** (targetLevel - 1)
 *
 * The result is a `bigint`. Ship level 10 is only 512 copies today, but the
 * formula is exponential and future categories may raise the cap; returning a
 * `number` would be a precision bug waiting to happen past level 54.
 */

import { assertSafeInteger, CARD_MAX_LEVEL } from '@kenocyber/shared';

export const MERGE_BRANCHING_FACTOR = 2n;

export const MAX_SHIP_LEVEL = 10;
/**
 * **O teto de um canhão: 5** (gate V28I.CH.11A, ADR 0138).
 *
 * Era 7. A salva mais larga passou de `7 x 3 = 21` corpos para `5 x 3 = 15`,
 * e continua a ser DERIVADA do catálogo — aqui e em mais lado nenhum. As três
 * raridades continuam Excellent, Perfect e Anomaly com 1, 2 e 3 projéteis por
 * canhão, e o quarto canhão continua cancelado (ADR 0132).
 *
 * **Uma instância antiga de nível 6 ou 7 não se apaga**: ela continua na base,
 * e o que muda é o teto por que ela é LIDA. Ver `effectiveItemLevel`.
 */
export const MAX_CANNON_LEVEL = 5;
export const MAX_ARTIFACT_LEVEL = 10;
/**
 * O teto de uma carta, LIDO de `cards.ts` (gate V28H.C).
 *
 * Lido e não escrito, ao contrário dos três acima, e a razão é que ele já tem
 * dono: `CARD_MAX_LEVEL` e a redução de cooldown por nível são as duas metades
 * de uma decisão só. Um `6` copiado para aqui seria a metade que ninguém
 * atualiza.
 */
export const MAX_CARD_LEVEL = CARD_MAX_LEVEL;

export const MERGEABLE_CATEGORIES = ['ship', 'cannon', 'artifact', 'card'] as const;
export type MergeableCategory = (typeof MERGEABLE_CATEGORIES)[number];

export const MAX_LEVEL_BY_CATEGORY: Readonly<Record<MergeableCategory, number>> = Object.freeze({
  ship: MAX_SHIP_LEVEL,
  cannon: MAX_CANNON_LEVEL,
  artifact: MAX_ARTIFACT_LEVEL,
  card: MAX_CARD_LEVEL,
});

function assertLevel(level: number, label = 'level'): void {
  assertSafeInteger(level, label);
  if (level < 1) {
    throw new RangeError(`${label} must be >= 1, received ${level}`);
  }
}

/** Level-1 copies required to craft a single item of `targetLevel`. */
export function requiredCopiesForLevel(targetLevel: number): bigint {
  assertLevel(targetLevel, 'targetLevel');
  return MERGE_BRANCHING_FACTOR ** BigInt(targetLevel - 1);
}

/** Copies *of `fromLevel`* required to craft a single item of `toLevel`. */
export function requiredCopiesBetweenLevels(fromLevel: number, toLevel: number): bigint {
  assertLevel(fromLevel, 'fromLevel');
  assertLevel(toLevel, 'toLevel');
  if (toLevel < fromLevel) {
    throw new RangeError(`toLevel (${toLevel}) must not be below fromLevel (${fromLevel})`);
  }
  return MERGE_BRANCHING_FACTOR ** BigInt(toLevel - fromLevel);
}

/** True when `level` is a legal level for that category. */
export function isLevelWithinCap(category: MergeableCategory, level: number): boolean {
  return Number.isSafeInteger(level) && level >= 1 && level <= MAX_LEVEL_BY_CATEGORY[category];
}

export interface MergePlan {
  readonly category: MergeableCategory;
  readonly fromLevel: number;
  readonly toLevel: number;
  readonly copiesRequired: bigint;
  readonly copiesAvailable: bigint;
  readonly canMerge: boolean;
  readonly copiesMissing: bigint;
}

/**
 * Pure planning helper. It never mutates an inventory: the authoritative merge
 * happens server-side inside a single database transaction.
 */
export function planMerge(
  category: MergeableCategory,
  fromLevel: number,
  copiesAvailable: bigint,
): MergePlan {
  assertLevel(fromLevel, 'fromLevel');
  if (copiesAvailable < 0n) {
    throw new RangeError('copiesAvailable must not be negative');
  }
  const toLevel = fromLevel + 1;
  const capped = !isLevelWithinCap(category, toLevel);
  const copiesRequired = capped ? 0n : MERGE_BRANCHING_FACTOR;
  const canMerge = !capped && copiesAvailable >= copiesRequired;
  return {
    category,
    fromLevel,
    toLevel,
    copiesRequired,
    copiesAvailable,
    canMerge,
    copiesMissing: canMerge || capped ? 0n : copiesRequired - copiesAvailable,
  };
}
